/**
 * Server Health Widget
 *
 * Live server monitoring panel for the services dashboard. Polls the auth-hub
 * `/api/system/metrics` endpoint and renders overall health plus CPU, memory,
 * and disk usage. Admin-only (the endpoint requires the admin role).
 */

import React from 'react';
import { Card } from './Card';
import { systemApi, getErrorMessage } from '../services/api';
import type { SystemMetrics } from '../types';

const POLL_INTERVAL_MS = 5000;

/** Human-readable byte sizes (1024-based). */
function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Compact duration like "5d 3h 12m". */
function formatUptime(seconds: number): string {
  if (!seconds || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
}

/** Color by usage pressure. */
function pressureColor(percent: number): string {
  if (percent >= 90) return '#dc2626'; // red
  if (percent >= 75) return '#f59e0b'; // amber
  return '#10b981'; // emerald
}

const STATUS_META: Record<
  SystemMetrics['status'],
  { label: string; color: string; bg: string }
> = {
  healthy: { label: 'Healthy', color: '#047857', bg: 'rgba(16,185,129,0.15)' },
  warning: { label: 'Warning', color: '#b45309', bg: 'rgba(245,158,11,0.15)' },
  critical: { label: 'Critical', color: '#b91c1c', bg: 'rgba(220,38,38,0.15)' },
};

const ResourceBar: React.FC<{ label: string; percent: number; detail: string }> = ({
  label,
  percent,
  detail,
}) => {
  const color = pressureColor(percent);
  const width = Math.max(0, Math.min(100, percent));
  return (
    <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '6px',
        }}
      >
        <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>
          {label}
        </span>
        <span style={{ fontSize: '14px', fontWeight: 600, color }}>{percent.toFixed(1)}%</span>
      </div>
      <div
        style={{
          height: '8px',
          width: '100%',
          borderRadius: '9999px',
          backgroundColor: 'var(--border-light)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${width}%`,
            borderRadius: '9999px',
            backgroundColor: color,
            transition: 'width 0.5s ease',
          }}
        />
      </div>
      <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
        {detail}
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ minWidth: '120px' }}>
    <div
      style={{
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--text-secondary)',
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: '14px',
        fontWeight: 500,
        color: 'var(--text-primary)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      title={value}
    >
      {value}
    </div>
  </div>
);

export const ServerHealthWidget: React.FC = () => {
  const [metrics, setMetrics] = React.useState<SystemMetrics | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const load = async () => {
      try {
        const data = await systemApi.getMetrics(controller.signal);
        if (cancelled) return;
        setMetrics(data);
        setError(null);
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const timer = setInterval(load, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
    };
  }, []);

  if (loading && !metrics) {
    return (
      <Card>
        <div style={{ color: 'var(--text-secondary)' }}>Loading server metrics…</div>
      </Card>
    );
  }

  if (error && !metrics) {
    return (
      <Card>
        <div style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>
          🖥️ Server Health
        </div>
        <div style={{ marginTop: '8px', fontSize: '14px', color: '#dc2626' }}>{error}</div>
      </Card>
    );
  }

  if (!metrics) return null;

  const status = STATUS_META[metrics.status];

  return (
    <Card>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          🖥️ Server Health
        </h2>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 12px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: 600,
            color: status.color,
            backgroundColor: status.bg,
          }}
        >
          <span
            style={{
              height: '8px',
              width: '8px',
              borderRadius: '9999px',
              backgroundColor: status.color,
            }}
          />
          {status.label}
        </span>
      </div>

      {/* Host summary */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginTop: '20px' }}>
        <Stat label="Hostname" value={metrics.host.hostname} />
        <Stat label="OS Uptime" value={formatUptime(metrics.host.uptimeSeconds)} />
        <Stat label="Platform" value={`${metrics.host.platform} ${metrics.host.arch}`} />
        <Stat label="CPU Cores" value={String(metrics.cpu.cores)} />
      </div>

      {/* Resource bars */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginTop: '24px' }}>
        <ResourceBar
          label="CPU"
          percent={metrics.cpu.usagePercent}
          detail={`Load: ${metrics.cpu.loadAverage['1m']} / ${metrics.cpu.loadAverage['5m']} / ${metrics.cpu.loadAverage['15m']} (1·5·15m)`}
        />
        <ResourceBar
          label="Memory"
          percent={metrics.memory.usedPercent}
          detail={`${formatBytes(metrics.memory.usedBytes)} / ${formatBytes(
            metrics.memory.totalBytes
          )} used`}
        />
        {metrics.disk.available ? (
          <ResourceBar
            label="Disk"
            percent={metrics.disk.usedPercent}
            detail={`${formatBytes(metrics.disk.usedBytes)} / ${formatBytes(
              metrics.disk.totalBytes
            )} used · ${formatBytes(metrics.disk.freeBytes)} free`}
          />
        ) : (
          <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
            <div
              style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '6px' }}
            >
              Disk
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Unavailable</div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border-light)',
          fontSize: '12px',
          color: 'var(--text-secondary)',
        }}
      >
        <span>
          Auth service uptime {formatUptime(metrics.process.uptimeSeconds)} · Node{' '}
          {metrics.process.nodeVersion}
        </span>
        <span>
          {error ? (
            <span style={{ color: '#f59e0b' }}>Reconnecting…</span>
          ) : (
            <>Updated {new Date(metrics.timestamp).toLocaleTimeString()}</>
          )}
        </span>
      </div>
    </Card>
  );
};
